import React, { FC, MouseEvent, useState } from 'react';
import { Button, Grid } from '@material-ui/core';
import DeleteIcon from '@material-ui/icons/Delete';
import { useTranslation } from 'react-i18next';
import { useFormikContext } from 'formik';
import { makeStyles } from '@material-ui/core/styles';
import classNames from 'classnames';
import { isEqual } from 'lodash';
import { useHistory, useParams } from 'react-router';

import { GroupStatus, UpdateGroupInput } from 'api';
import { Alert, Loaders } from 'components';
import { useCurrentUser } from 'hooks';
import { isAdminUser } from 'helpers';
import { useGroupData } from 'features/groups/hooks';

import { getGroupHexColor, isGroupOwner } from '../../helpers';

interface IProps {
  isWorking: boolean;
}

interface IGroupParams {
  group_id: string;
}

const useStyles = makeStyles(theme => ({
  button: {
    borderRadius: 4,
  },
  doneButton: {
    marginLeft: theme.spacing(2),
    transition: 'opacity 150ms ease-in-out',
    borderColor: 'transparent',
    color: theme.palette.common.white,
    '&:hover': {
      opacity: 0.9,
      borderColor: 'transparent',
    },
  },
}));

const EditActions: FC<IProps> = ({ isWorking }) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const { values, setFieldValue, isValid, submitForm, dirty } =
    useFormikContext<UpdateGroupInput>();
  const { groupColors } = useGroupData();
  const { group_id } = useParams<IGroupParams>();
  const currentUser = useCurrentUser();
  const history = useHistory();

  const [initialValues] = useState(values);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [isReadOnlyOpen, setIsReadOnlyOpen] = useState(false);
  const [isPrivateOpen, setIsPrivateOpen] = useState(false);
  const [isCancelOpen, setIsCancelOpen] = useState(false);

  const hexColor = getGroupHexColor(groupColors, values.color);
  const isPrivateUpdated = !isEqual(values.is_private, initialValues.is_private);
  const isReadOnlyUpdated = !isEqual(values.read_only, initialValues.read_only);

  function toggleIsCancel() {
    setIsCancelOpen(!isCancelOpen);
  }

  function toggleIsReadOnly() {
    setIsReadOnlyOpen(!isReadOnlyOpen);
  }

  function toggleIsPrivate() {
    setIsPrivateOpen(!isPrivateOpen);
  }

  function onClickDeleteToggle() {
    setIsDeleteOpen(!isDeleteOpen);
  }

  function onClickSave(event: MouseEvent<HTMLButtonElement>) {
    event.preventDefault();

    if (isReadOnlyUpdated) {
      toggleIsReadOnly();
      return null;
    }

    if (isPrivateUpdated) {
      toggleIsPrivate();
      return null;
    }

    return submitForm();
  }

  function handleDelete() {
    setIsDeleteOpen(false);
    setFieldValue('group_status', GroupStatus.Deleted);
    return submitForm().then(() => history.push('/groups'));
  }

  function handleReadOnlyConfirm() {
    if (isPrivateUpdated) {
      toggleIsPrivate();
      return null;
    }

    return submitForm();
  }

  function handleReadOnlyCancel() {
    toggleIsReadOnly();

    if (isPrivateUpdated) {
      toggleIsPrivate();
    }
  }

  function renderDirty() {
    return (
      <Grid container justify="flex-end" alignItems="center">
        <Grid item>
          <Button
            onClick={toggleIsCancel}
            size="large"
            className={classes.button}
            variant="outlined"
          >
            {t('common:cancel')}
          </Button>

          <Alert
            open={isCancelOpen}
            onConfirm={history.goBack}
            onCancel={toggleIsCancel}
            message={t('groups:cancelMessage')}
            confirm={t('groups:discardEdit')}
          />
        </Grid>

        <Grid item>
          <Button
            disabled={!isValid}
            size="large"
            style={{ backgroundColor: hexColor }}
            className={classNames(classes.button, classes.doneButton)}
            onClick={onClickSave}
            variant="contained"
          >
            {isWorking ? <Loaders.Standard height={26} /> : t('common:save')}
          </Button>
        </Grid>

        <Alert
          open={isReadOnlyOpen}
          onConfirm={handleReadOnlyConfirm}
          onCancel={handleReadOnlyCancel}
          message={
            initialValues.read_only ? t('groups:allowSharingMessage') : t('groups:readOnlyMessage')
          }
          confirm={initialValues.read_only ? t('groups:allowSharing') : t('groups:readOnlyConfirm')}
        />

        <Alert
          open={isPrivateOpen}
          onConfirm={submitForm}
          onCancel={toggleIsPrivate}
          message={
            initialValues.is_private
              ? t('groups:makePublicMessage')
              : t('groups:makePrivateMessage')
          }
          confirm={initialValues.is_private ? t('groups:makePublic') : t('groups:makePrivate')}
        />
      </Grid>
    );
  }

  if (dirty) {
    return renderDirty();
  }

  const canDelete = isAdminUser(currentUser) || isGroupOwner(currentUser, group_id);

  return (
    <Grid container justify={canDelete ? 'space-between' : 'flex-end'} alignItems="center">
      {canDelete && (
        <Grid item>
          <Button startIcon={<DeleteIcon />} onClick={onClickDeleteToggle}>
            {t('groups:deleteGroup')}
          </Button>

          <Alert
            open={isDeleteOpen}
            onConfirm={handleDelete}
            onCancel={onClickDeleteToggle}
            message={t('groups:deleteGroupAlert')}
            confirm={t('common:delete')}
          />
        </Grid>
      )}

      <Grid item>
        <Button
          disabled={!isValid}
          className={classNames(classes.button, classes.doneButton)}
          variant="contained"
          size="large"
          onClick={history.goBack}
          style={{ backgroundColor: hexColor }}
        >
          {t('common:done')}
        </Button>
      </Grid>
    </Grid>
  );
};

export default EditActions;
